<template>
  <div class="challenge-match-tab">
    <div class="challenge-match-filters">
      <label-filter
        icon="star"
        class="challenge-match-filters__filter"
        text="Shortlist"
        :is-enabled="shortListingFilterEnabled"
        @updateFilter="shortListingFilterEnabled = !shortListingFilterEnabled"
      />
      <label-filter
        class="challenge-match-filters__filter"
        text="Recommendations"
        :is-enabled="!shortListingFilterEnabled"
        @updateFilter="shortListingFilterEnabled = !shortListingFilterEnabled"
      />
    </div>

    <div class="challenge-actor-suggestions" v-if="!shortListingFilterEnabled">
      <h3>{{ $t('challenge_recommended_actors_title') }}</h3>

      <Loading v-if="loadingRecommendations" />

      <template v-else>
        <div v-if="displayRecommendedActors && challenge.matchmakingTaskStatus === 'completed'" class="challenge-match-section">
          <div class="tw-flex flex-wrap gap-4">
            <ActorSuggestionCardNew
              v-for="matchingActor in agenticRecommendedActors"
              :key="matchingActor.id"
              class="challenge-actor-suggestion"
              :actor="matchingActor"
              :loading="viewShortListedActors"
              @toggle-shortlist-status="toggleActorShortListStatus"
              @manage-invitees="openManageInviteesSidePanel"
            />
          </div>
        </div>

        <div class="challenge-match-section" v-else-if="challenge.matchmakingTaskStatus !== 'completed'">
          {{ $t('challenge_matchmaking_in_progress') }}
        </div>

        <div class="challenge-match-section" v-else>
          {{ $t('challenge_matchmaking_no_recommended_actors') }}
        </div>

        <div class="challenge-match-section" v-if="interestingActors.length > 0">
          <h3>{{ $t('challenge_interesting_actors_title') }}</h3>
          <div class="tw-flex flex-wrap gap-4">
            <ActorSuggestionCardNew
              v-for="matchingActor in interestingActors"
              :key="matchingActor.id"
              class="challenge-actor-suggestion"
              :actor="matchingActor"
              @toggle-shortlist-status="toggleActorShortListStatus"
              :loading="viewShortListedActors"
              @manage-invitees="openManageInviteesSidePanel"
            />
          </div>

          <!--<div v-else>
            {{ $t('challenge_matchmaking_no_interesting_actors') }}
          </div>-->
        </div>
      </template>
    </div>

    <div class="challenge-actor-suggestions" v-else>
      <Loading v-if="loadingShortlistedActors" />

      <template v-else>
        <div v-if="shortListedActors.length > 0">
          <ActorSuggestionCardNew
            v-for="matchingActor in shortListedActors"
            :key="matchingActor.id"
            class="challenge-actor-suggestion"
            :actor="matchingActor"
            :loading="viewShortListedActors"
            @manage-invitees="openManageInviteesSidePanel"
            @toggle-shortlist-status="toggleActorShortListStatus"
          />
        </div>

        <div v-else>
          {{ $t('challenge_no_shortlisted_actors') }}
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import ActorSuggestionCardNew from '@/Domain/Actor/Components/ActorSuggestionCardNew.vue'
  import LabelFilter from '@/components/Filters/LabelFilter.vue'
  import Loading from '@/components/Dashboard/ConceptMap/Loading.vue'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import { addShortListedActor, removeShortListedActor, getMatchingActors, getShortListedActors } from '@/Domain/Challenge/Api/challenges'
  import { convertToFilterObject, stringifyProperties } from '@/store/modules/filters'
  import { transformChallengeActorRecommendationsToChallengeActorArray } from '@/Domain/Challenge/Api/challenge-api-transformers'
  import { ActorChallengeRecommendation } from '@/Domain/Challenge/Types/challenge-types'
  import { Conversations } from '@/api/conversations'
  import { ACTION_TYPES as CONVERSATION_ACTION_TYPES } from '@/store/modules/conversations'

  export default defineComponent({
    name: 'ChallengeMatchTab',
    components: {
      ActorSuggestionCardNew,
      LabelFilter,
      Loading,
    },
    props: {
      challengeId: {
        type: Number,
        required: true,
      },
      challenge: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        shortListingFilterEnabled: false,
        viewShortListedActors: false,
        actorRecommendations: [],
        actorRecommendationFilters: [],
        loadingRecommendations: false,
        loadingShortlistedActors: false,
        shortListedActors: [],
        // Store handler references for proper cleanup
        handleInviteesUpdated: null,
        handleInviteesInvited: null,
      }
    },
    computed: {
      displayRecommendedActors(): boolean {
        return this.agenticRecommendedActors.length > 0
      },
      interestingActors () : Array<ActorChallengeRecommendation> {
        return this.actorRecommendations.filter((actor) => !actor.suggestedThroughAgenticMatchMaking && !actor.isActorShortListed)
      },
      agenticRecommendedActors () : Array<ActorChallengeRecommendation> {
        return this.actorRecommendations.filter((actor) => actor.suggestedThroughAgenticMatchMaking && !actor.isActorShortListed)
      },
    },
    methods: {
      openManageInviteesSidePanel() {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
          component: 'manage-challenge-invitees',
          metaData: this.challenge,
        })
      },
      async toggleActorShortListStatus(actor) {
        this.viewShortListedActors = true

        try {
          if (!actor.isActorShortListed) {
            await addShortListedActor(this.challengeId, actor.id)
          } else {
            await removeShortListedActor(this.challengeId, actor.id)
          }

          actor.isActorShortListed = !actor.isActorShortListed

          // Refetch shortlisted actors after toggle
          await this.fetchShortListedActors()
        } catch (error) {
          console.error('Failed to toggle shortlist status:', error)
        } finally {
          this.viewShortListedActors = false
        }
      },
      fetchMatchingActors() {
        const stringifiedFilterObject = stringifyProperties(convertToFilterObject(this.actorRecommendationFilters))
        this.loadingRecommendations = true

        getMatchingActors(this.challengeId, stringifiedFilterObject)
          .then((result) => {
            this.actorRecommendations = transformChallengeActorRecommendationsToChallengeActorArray(result)
          })
          .finally(() => {
            this.loadingRecommendations = false
          })
      },
      fetchShortListedActors() {
        this.loadingShortlistedActors = true

        return getShortListedActors(this.challengeId)
          .then((result) => {
            this.shortListedActors = transformChallengeActorRecommendationsToChallengeActorArray(result)
          })
          .catch((error) => {
            console.error('Failed to fetch shortlisted actors:', error)
          })
          .finally(() => {
            this.loadingShortlistedActors = false
          })
      }
    },
    mounted() {
      this.fetchMatchingActors()
      this.fetchShortListedActors()

      // Create and store handler references for proper cleanup
      this.handleInviteesUpdated = () => {
        this.fetchShortListedActors()
      }

      this.handleInviteesInvited = async (actor) => {
        await addShortListedActor(this.challengeId, actor)
        this.fetchShortListedActors()
      }

      // Listen for events that require refetching matching actors
      this.$bus.on('challengeInviteesUpdated', this.handleInviteesUpdated)
      this.$bus.on('challengeInviteesInvited', this.handleInviteesInvited)
    },
    beforeUnmount() {
      // Clean up only this component's listeners
      if (this.handleInviteesUpdated) {
        this.$bus.off('challengeInviteesUpdated', this.handleInviteesUpdated)
      }
      if (this.handleInviteesInvited) {
        this.$bus.off('challengeInviteesInvited', this.handleInviteesInvited)
      }
    },
    watch: {
      challengeId() {
        // Refetch both matching actors and shortlisted actors when challenge ID changes
        this.fetchMatchingActors()
        this.fetchShortListedActors()
      },
    },
  })
</script>

<style lang="scss" scoped>
  .challenge-match-tab {
    padding: 1rem;
  }

  .challenge-match-filters {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    margin-bottom: 1rem;
    align-items: center;

    &__label {
      display: flex;
      align-content: center;
      flex-wrap: wrap;
    }

    &__filter {
      margin-left: 0.5rem;
    }
  }

  .challenge-match-section {
    width: 100%;
    margin-bottom: 2rem;

    h3 {
      margin-bottom: 1rem;
    }
  }

  .challenge-actor-suggestions {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1rem;

    .challenge-actor-suggestion {
      width: 250px;
    }
  }
</style>
